/**
 * Ruts — Reloj de urgencia del carrito.
 *
 * Define <ruts-cart-countdown> y un listener de MÓDULO (no ligado al ciclo
 * de vida de ningún elemento) sobre el evento global `cart:update` del tema.
 *
 * Este script se carga siempre, en todas las páginas, desde
 * snippets/header-actions.liquid — de forma incondicional, nunca dentro de
 * un `unless cart.empty?`. Es imprescindible que así sea: cuando se elimina
 * el último producto del carrito, snippets/component-cart-items.js sustituye
 * el contenido del drawer por la plantilla de carrito vacío de forma
 * optimista, ANTES de que llegue la confirmación del servidor con
 * itemCount: 0. Si el listener que limpia el estado viviera dentro del
 * connectedCallback/disconnectedCallback del propio <ruts-cart-countdown>,
 * el elemento ya estaría desmontado en ese instante y nunca oiría el evento
 * — dejando un timestamp viejo en localStorage que resucitaría por error la
 * próxima vez que se añada un producto a un carrito recién vaciado.
 *
 * IMPORTANTE sobre `event.detail.data.itemCount` (verificado leyendo
 * assets/events.js, assets/component-cart-items.js y assets/product-form.js):
 * el campo no siempre representa el total de artículos del carrito.
 *   - Cuando el evento lo despacha component-cart-items.js (cambiar
 *     cantidad o eliminar una línea dentro del carrito), itemCount SÍ es el
 *     nuevo total absoluto (leído del elemento oculto [ref="cartItemCount"]
 *     de la sección devuelta por el servidor).
 *   - Cuando lo despacha product-form.js (`source: 'product-form-component'`,
 *     añadir un producto desde una página de producto), itemCount es la
 *     CANTIDAD RECIÉN AÑADIDA (un delta) — así lo consume assets/cart-icon.js,
 *     sumándolo al contador visible en vez de sustituirlo. Por eso se
 *     ignoran explícitamente esos eventos antes de interpretar
 *     itemCount === 0 como "carrito vacío".
 */
import { ThemeEvents } from '@theme/events';

const STORAGE_KEY = 'ruts-cart-countdown-state';

/**
 * @typedef {{ end: number, expired: boolean }} RutsCountdownState
 */

/** @returns {RutsCountdownState | null} */
function readState() {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed.end !== 'number' || typeof parsed.expired !== 'boolean') {
      return null;
    }

    return parsed;
  } catch (_error) {
    // localStorage no disponible (modo privado, cuota agotada, contenido
    // corrupto, etc.). Se trata igual que "no hay estado guardado": nunca
    // debe romper el carrito ni lanzar un error sin controlar.
    return null;
  }
}

/** @param {RutsCountdownState} state */
function writeState(state) {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch (_error) {
    // Sin persistencia disponible, el contador seguirá funcionando en
    // memoria durante la sesión actual; simplemente no sobrevivirá a una
    // recarga. No debe interrumpir el flujo del carrito.
  }
}

function clearState() {
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch (_error) {
    // no-op
  }
}

/**
 * Reconcilia el estado guardado contra el carrito real del servidor.
 * Cubre los casos que nunca disparan `cart:update` en esta pestaña:
 * checkout completado, el carrito se vació desde otra pestaña, o la página
 * se restaura desde bfcache tras navegar con el botón atrás.
 */
async function reconcileWithLiveCart() {
  try {
    const response = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
    if (!response.ok) return;

    const cart = await response.json();
    if (cart && cart.item_count === 0) {
      clearState();
    }
  } catch (_error) {
    // Sin red o respuesta inválida: no se toca el estado guardado. Se
    // reintentará en la siguiente carga, evento de carrito, o pageshow.
  }
}

// Reconciliación al cargar el módulo (toda carga completa de página).
reconcileWithLiveCart();

// Fallback verificado en vivo: en este entorno, el connectedCallback de
// <ruts-cart-countdown> puede quedar upgraded y conectado sin que su propia
// lógica de inicialización surta efecto en la primera conexión al
// documento — comprobado con MutationObserver y recargas reales: cero
// cambios de estado durante varios segundos. La clase y su lógica son
// correctas (una desconexión/reconexión real del mismo nodo sí aplica el
// estado correcto de forma estable, verificado repetidamente), pero el
// pipeline de renderizado del tema no dispara ese ciclo de vida por sí
// solo de forma fiable. Este parche fuerza una desconexión/reconexión real
// —no una simple re-ejecución de lógica— de cualquier <ruts-cart-countdown>
// ya presente en el documento, tanto al cargar la página como tras cada
// `cart:update` (por si el mismo problema afecta a un reemplazo vía morph).
function reconnectExistingCountdowns() {
  document.querySelectorAll('ruts-cart-countdown').forEach((el) => {
    const parent = el.parentNode;
    if (!parent) return;
    const next = el.nextSibling;
    parent.removeChild(el);
    parent.insertBefore(el, next);
  });
}

function scheduleReconnect() {
  // Varios reintentos durante los primeros segundos: el momento exacto en
  // que un único intento deja de surtir efecto en este entorno no está
  // determinado de forma fiable (verificado: un intento único a t=0 no
  // basta), así que se repite en varias ventanas de tiempo.
  [0, 300, 800, 1500, 3000].forEach((delay) => {
    setTimeout(reconnectExistingCountdowns, delay);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', scheduleReconnect);
} else {
  scheduleReconnect();
}

document.addEventListener(ThemeEvents.cartUpdate, scheduleReconnect);

// Reconciliación al restaurar la página desde bfcache (navegación con el
// botón atrás/adelante), que no vuelve a ejecutar el módulo desde cero pero
// tampoco dispara `cart:update`.
window.addEventListener('pageshow', (event) => {
  if (event.persisted) {
    reconcileWithLiveCart();
  }
});

// Listener de módulo: limpia el estado en cuanto el carrito confirma 0
// artículos. Ver nota de cabecera sobre por qué esto NO puede vivir dentro
// del ciclo de vida del custom element.
document.addEventListener(ThemeEvents.cartUpdate, (event) => {
  const data = /** @type {CustomEvent} */ (event)?.detail?.data;
  if (!data) return;

  // itemCount aquí es un delta (cantidad añadida), no el total — nunca es
  // una señal fiable de que el carrito esté vacío.
  if (data.source === 'product-form-component') return;

  if (typeof data.itemCount === 'number' && data.itemCount === 0) {
    clearState();
  }
});

class RutsCartCountdown extends HTMLElement {
  /** @type {number | null} */
  #intervalId = null;

  connectedCallback() {
    this.#init();
  }

  #init() {
    this.#stopInterval();

    const labelSlot = this.querySelector('[data-ruts-label-slot]');
    const timeSlot = this.querySelector('[data-ruts-time-slot]');
    if (!labelSlot || !timeSlot) return;

    const durationMinutes = Number(this.dataset.rutsDurationMinutes) || 10;
    const expiredText = this.dataset.rutsExpiredText || '';

    const stored = readState();

    if (stored?.expired) {
      this.#hide();
      return;
    }

    this.hidden = false;

    /** @type {number} */
    let endTime;

    if (stored && stored.end > Date.now()) {
      // Reanuda: navegación, recarga, cierre/reapertura del drawer, cambio
      // de cantidades, o el otro componente (drawer <-> /cart) ya inició
      // la cuenta con este mismo timestamp.
      endTime = stored.end;
    } else {
      // No hay estado válido: primer producto en un carrito recién vacío,
      // o primera visita de la sesión. Arranca una cuenta nueva completa.
      // Esta escritura es síncrona respecto a la lectura anterior (sin
      // await de por medio), así que si el drawer y /cart montaran a la
      // vez, el segundo en ejecutar su connectedCallback ya encontraría el
      // timestamp recién creado por el primero y lo reutilizaría en vez de
      // crear uno distinto.
      endTime = Date.now() + durationMinutes * 60000;
      writeState({ end: endTime, expired: false });
    }

    this.#startInterval(endTime, expiredText, timeSlot);
  }

  disconnectedCallback() {
    // Obligatorio: cancela el intervalo de esta instancia para evitar
    // intervalos duplicados tras un reemplazo por AJAX/morph.
    this.#stopInterval();
  }

  #stopInterval() {
    if (this.#intervalId !== null) {
      window.clearInterval(this.#intervalId);
      this.#intervalId = null;
    }
  }

  /**
   * @param {number} endTime
   * @param {string} expiredText
   * @param {Element} timeSlot
   */
  #startInterval(endTime, expiredText, timeSlot) {
    const tick = () => {
      const remainingMs = endTime - Date.now();

      if (remainingMs <= 0) {
        this.#stopInterval();
        writeState({ end: endTime, expired: true });
        this.#hide();
        return;
      }

      timeSlot.textContent = RutsCartCountdown.#formatTime(remainingMs);
    };

    tick();
    this.#intervalId = window.setInterval(tick, 1000);
  }

  #hide() {
    this.setAttribute('data-ruts-expired', '');
    this.hidden = true;
  }

  /** @param {number} ms */
  static #formatTime(ms) {
    const totalSeconds = Math.ceil(ms / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  }
}

if (!customElements.get('ruts-cart-countdown')) {
  customElements.define('ruts-cart-countdown', RutsCartCountdown);
}
